import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from './supabase'
import { saveReferenceCache, loadReferenceCache, clearReferenceCache } from './offlineStore'

/**
 * useArchCatalogItems — fetches arch_catalog_items from Supabase.
 * @param {object|null} session
 */
export function useArchCatalogItems(session, { phase = null, projectType = null } = {}) {
    const [items,   setItems]   = useState([])
    const [loading, setLoading] = useState(true)
    const [error,   setError]   = useState(null)
    const userId = session?.user?.id || null
    const loadIdRef = useRef(0)

    const fetchAndApply = useCallback(async (id, silent) => {
        if (!silent) {
            setLoading(true)
        }
        setError(null)
        // Serve from cache immediately (stale-while-revalidate)
        const cached = await loadReferenceCache('arch_catalog_items', userId).catch(() => null)
        if (cached) {
            setItems(cached.data)
            if (!silent) setLoading(false)
        }
        let q = supabase
            .from('arch_catalog_items')
            .select('*')
            .order('project_type', { ascending: true })
            .order('discipline',   { ascending: true })
            .order('name',         { ascending: true })
        if (phase)       q = q.eq('phase',        phase)
        if (projectType) q = q.eq('project_type', projectType)
        const { data, error: err } = await q
        if (id !== loadIdRef.current) return
        if (err) {
            console.error('[archCatalogItems] fetch error:', err.message)
            setError(err.message)
            if (!cached) setItems([])
        } else {
            setItems(data || [])
            await saveReferenceCache('arch_catalog_items', data || [], userId).catch(() => {})
        }
        if (!silent) setLoading(false)
    }, [userId, phase, projectType])

    const load = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, false)
    }, [fetchAndApply])

    const silentReload = useCallback(async () => {
        const id = ++loadIdRef.current
        await fetchAndApply(id, true)
    }, [fetchAndApply])

    useEffect(() => { load() }, [load])

    /** Upsert a full item row. Optimistic local update. */
    const upsertArchCatalogItem = useCallback(async (item) => {
        const payload = {
            id:           item.id || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
            name:         item.name,
            discipline:   item.discipline || 'GENERAL',
            subcat:       item.subcat       ?? null,
            phase:        item.phase        ?? null,
            project_type: item.project_type ?? null,
            is_custom:    item.is_custom ?? true,
            updated_by:   userId,
            updated_at:   new Date().toISOString(),
        }
        if (!item.id) {
            payload.created_by = userId
        }
        setItems(prev => {
            const idx = prev.findIndex(i => i.id === payload.id)
            if (idx === -1) return [...prev, payload]
            const next = [...prev]; next[idx] = payload; return next
        })
        const { data, error: err } = await supabase
            .from('arch_catalog_items')
            .upsert(payload, { onConflict: 'id' })
            .select()
            .single()
        if (err) {
            console.error('[archCatalogItems] upsert error:', err.message)
            throw new Error(err.message)
        }
        setItems(prev => {
            const idx = prev.findIndex(i => i.id === data.id)
            if (idx === -1) return [...prev, data]
            const next = [...prev]; next[idx] = data; return next
        })
        await clearReferenceCache('arch_catalog_items', userId).catch(() => {})
        return data
    }, [userId])

    /** Delete a catalog item. System-seeded items (is_custom=false) are protected. */
    const removeArchCatalogItem = useCallback(async (id) => {
        const item = items.find(i => i.id === id)
        if (item && item.is_custom === false) {
            throw new Error('System catalog items cannot be deleted.')
        }
        setItems(prev => prev.filter(i => i.id !== id))
        const { error: err } = await supabase
            .from('arch_catalog_items')
            .delete()
            .eq('id', id)
        if (err) {
            console.error('[archCatalogItems] delete error:', err.message)
            throw new Error(err.message)
        }
        await clearReferenceCache('arch_catalog_items', userId).catch(() => {})
    }, [items, userId])

    return { items, loading, error, upsertArchCatalogItem, removeArchCatalogItem, reload: load, silentReload }
}
